// ─────────────────────────────────────────────────────────────────────────────
// System-wide sensitive-figures shield (Karan 07/08).
//
// The genuinely private money in Track — break-even cost, margin, annual salary,
// charge-out rate, bank balances — is HIDDEN BY DEFAULT on every load and shown
// only while the viewer flips the top-bar "Figures" toggle on. Everyday
// operational amounts (a project's fee, an invoice total, an expense) are NOT
// covered here and stay visible.
//
// Three rules:
//   1. Default hidden. Every session starts masked; reveal is never persisted, so
//      a reload re-hides. A 15-minute idle backstop re-locks if you walk away.
//   2. Reveal is bounded by ROLE. This shield only masks figures the viewer is
//      ALREADY allowed to see — a value your permissions don't grant is emitted
//      as the mask directly by the view (never wrapped with the real value), so
//      revealing can't leak it. This is discretion from glancing eyes, not a
//      security boundary (that stays in the Firestore rules + role gates).
//   3. One control. This replaces the per-screen Finance frost (privacy.js): the
//      reveal state is global and holds across navigation within a session.
//
// Masking itself is CSS-only (see .figures-hidden .secret in styles.css): the
// real value sits in the DOM inside a <span class="secret"> and is covered while
// hidden, so toggling never needs a re-render.
// ─────────────────────────────────────────────────────────────────────────────

const RELOCK_MINUTES = 15;
let revealed = false;                 // session-only; always starts hidden
let relockTimer = null;
const listeners = new Set();

export function figuresRevealed() { return revealed; }

// Subscribe to reveal/hide changes (top-bar button uses this to update its own
// label; views generally don't need it because masking is CSS-driven).
export function onFiguresChange(fn) {
  if (typeof fn === 'function') listeners.add(fn);
  return () => listeners.delete(fn);
}

function apply() {
  document.body.classList.toggle('figures-hidden', !revealed);
  listeners.forEach((fn) => { try { fn(revealed); } catch (_) { /* best-effort */ } });
}

export function revealFigures() {
  revealed = true;
  clearTimeout(relockTimer);
  relockTimer = setTimeout(hideFigures, RELOCK_MINUTES * 60 * 1000);
  apply();
}

export function hideFigures() {
  revealed = false;
  clearTimeout(relockTimer);
  apply();
}

export function toggleFigures() { revealed ? hideFigures() : revealFigures(); }

// Call once at startup so the page begins masked (before any view renders).
export function initFiguresShield() {
  revealed = false;
  document.body.classList.add('figures-hidden');
}
